import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { getApiErrorMessage } from '@/utils/api-error';

interface UseNotesEditorOptions {
  /** Initial notes value from API */
  initialNotes: string | null;
  /** Async callback to save notes */
  onSave: (notes: string) => Promise<void>;
}

interface UseNotesEditorResult {
  /** Current value in the textarea */
  notesValue: string;
  /** Whether editor is in editing mode */
  isEditing: boolean;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Start editing (resets to current API value) */
  startEditing: () => void;
  /** Update the textarea value */
  setNotesValue: (value: string) => void;
  /** Save notes to API */
  saveNotes: () => Promise<void>;
  /** Cancel editing (reverts to initial value) */
  cancelEditing: () => void;
}

/**
 * Reusable hook for managing workflow notes editing state.
 *
 * Handles:
 * - Local state during active editing
 * - Async save with loading state
 * - Error handling with toast notifications
 * - Resetting state when starting/canceling edit
 */
export const useNotesEditor = ({
  initialNotes,
  onSave,
}: UseNotesEditorOptions): UseNotesEditorResult => {
  const [notesValue, setNotesValue] = useState(initialNotes ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = useCallback(() => {
    // Reset to current API value when starting edit
    setNotesValue(initialNotes ?? '');
    setIsEditing(true);
  }, [initialNotes]);

  const cancelEditing = useCallback(() => {
    setNotesValue(initialNotes ?? '');
    setIsEditing(false);
  }, [initialNotes]);

  const saveNotes = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSave(notesValue);
      toast.success('Note saved');
      setIsEditing(false);
    } catch (error) {
      toast.error(`Failed to save note: ${getApiErrorMessage(error)}`);
      // Keep editor open on error so user can retry
    } finally {
      setIsSaving(false);
    }
  }, [notesValue, onSave]);

  return {
    notesValue,
    isEditing,
    isSaving,
    startEditing,
    setNotesValue,
    saveNotes,
    cancelEditing,
  };
};
